import { UscCheckoutType } from "../../shared-logic/types/UscCommonTypes";
import { DfVehicleStatus } from "../../shared-logic/utils/dfConstants";
import { clearAllModals, showModal } from "../../../common-deprecated/redux/actions/ModalActions";
import { MODAL_USC_FORM } from "../../shared-logic/utils/integrated-modal/integratedModalConstants";
import { getUscUrl, getUscVehicleUrlInfoFromVehicle } from "../../shared-logic/utils/uscUtils";
import { type DealerResultType, DealerOperation } from "../../../common-deprecated/utils/dealerConstants";
import { setCheckoutType } from "../redux/actions/UscBasketActions";
import { DealerInfoType, UscNavStepEnum } from "./constants";
import { type UscLabelType, getUscLabel } from "./labels";
import { MODAL_USC_MOBILE_NAV_CTA } from "./modalConstants";
import { type UscNavigationParamsType, getUscInternalLink } from "./navUtils";
import { trackAemContactDealerCtas, trackCta, trackReserveCheckoutCta, UscTrackingLocation } from "./tracking";
import type { UscSettingsType } from "../redux/reducers/UscSettingsReducer";
import type { FinanceOptionType } from "../../../common-deprecated/types/CommonTypes";
import { getLinkToLocalReservation } from "./helpers";

export enum UscCtaIcon {
    Phone = "phone",
}

export enum UscCtaTypeEnum {
    // Configurable by NMSC's
    Buy = "buy",
    Reserve = "reserve",
    ContactDealer = "contactDealer",
    CallDealer = "callDealer",
    // Not configurable by NMSC's
    BackToCarFilter = "backToCarFilter",
    NavToCheckout = "navToCheckout",
}

export type UscCtaType = {
    label: string;
    icon?: UscCtaIcon;
    onClick?: (event: React.SyntheticEvent) => void;
    href?: string;
    type?: UscCtaTypeEnum;
    // Defines the relationship between the current document and the linked document.
    // Accepts a list of space-separated unique keywords that describe the nature of the relationship.
    // See: https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/rel
    rel?: string;
};

// ----------------------------------------------------------------------
// Individual BasketCtaType functions
// ----------------------------------------------------------------------

export const getNavToExtrasCta = (params: UscNavigationParamsType, checkoutType: UscCheckoutType): UscCtaType => {
    const isReserve = checkoutType === UscCheckoutType.Reserve;

    const { dispatch, activeFinanceOption, context, uscSettings, commonSettings, trackingLocation } = params;

    let nextLabelKey: UscLabelType;
    if (isReserve) {
        nextLabelKey = activeFinanceOption === "cash" ? "uscReserveThisCarCtaCash" : "uscReserveThisCarCtaMonthly";
    } else {
        nextLabelKey = activeFinanceOption === "cash" ? "uscBuyThisCarCtaCash" : "uscBuyThisCarCtaMonthly";
    }

    const nextLabel = getUscLabel({ uscSettings, commonSettings }, nextLabelKey);
    const link = getUscInternalLink(params, UscNavStepEnum.Extras, checkoutType);

    return {
        label: nextLabel,
        href: link.href,
        onClick: (event) => {
            // Hide all modals (fe. basket details modal) when moving to the extras step
            dispatch(clearAllModals());

            dispatch(setCheckoutType(isReserve ? UscCheckoutType.Reserve : UscCheckoutType.Buy));
            trackCta("services-more-infos", trackingLocation, context, false);
            if (link.onClick) link.onClick(event);
        },
        type: isReserve ? UscCtaTypeEnum.Reserve : UscCtaTypeEnum.Buy,
        rel: "nofollow",
    };
};

export const getNavToCheckoutCta = (params: UscNavigationParamsType): UscCtaType => {
    const { activeFinanceOption, context, uscSettings, commonSettings, checkoutType, trackingLocation } = params;
    const { checkoutCallback, dispatch } = params;
    const [openReserveCheckoutLabel, openBuyCheckoutLabel] = getUscLabel({ uscSettings, commonSettings }, [
        "uscBasketOpenReserveCheckoutCta",
        "uscBasketOpenBuyCheckoutCta",
    ]);

    const openCheckoutLabel = checkoutType === UscCheckoutType.Buy ? openBuyCheckoutLabel : openReserveCheckoutLabel;

    return {
        label: openCheckoutLabel,
        href: "#",
        onClick: () => {
            // Hide all modals (fe. basket details modal) before opening the checkout flow
            dispatch(clearAllModals());

            trackReserveCheckoutCta(trackingLocation, activeFinanceOption, context);
            checkoutCallback();
        },
    };
};

export const getNavToLocalReservationCta = (params: UscNavigationParamsType): UscCtaType | null => {
    const { uscSettings, commonSettings, vehicle, activeFinanceOption } = params;

    const nextLabel = getUscLabel(
        { uscSettings, commonSettings },
        activeFinanceOption === "cash" ? "uscLocalReserveThisCarCtaCash" : "uscLocalReserveThisCarCtaMonthly",
    );

    if (!vehicle) return null;

    return {
        label: nextLabel,
        href: getLinkToLocalReservation(vehicle, uscSettings.localOnlineReservationUrl),
        type: UscCtaTypeEnum.Reserve,
    };
};

export const getNavToFilterCta = (params: UscNavigationParamsType): UscCtaType => {
    const { uscSettings, commonSettings, carFilterUrl } = params;
    const toFilterLabel = getUscLabel({ uscSettings, commonSettings }, "uscBasketCtaToFilter");

    return {
        label: toFilterLabel,
        href: carFilterUrl,
        type: UscCtaTypeEnum.BackToCarFilter,
    };
};

/**
 * Make sure to align the logic in this function with the Car Filter equivalent.
 */
export const getContactDealerCta = (params: UscNavigationParamsType): UscCtaType | null => {
    const { uscSettings, commonSettings, vehicle, context, trackingLocation, dispatch } = params;
    const { dealer, localContactDealerClick } = params;

    // In the case of a hub car, we won't have a dealer. This is fine, and is handled by sending the dealer as undefined (see below).
    // For non-hub cars, we _need_ a dealer to show the contact dealer form. If we don't have a dealer, we don't show the CTA.
    if (!vehicle?.isHubCar && !dealer) {
        return null;
    }

    const contactLabel = getUscLabel({ uscSettings, commonSettings }, "uscContactDealerCta");

    let contactDealerClick = null;

    const trackingPartialComponent = "vehicle-request";

    const tracking = (): void => {
        trackCta(trackingPartialComponent, trackingLocation, context);
    };

    if (uscSettings.localContactDealerFormUrl && !commonSettings.query.disableLocalContactForm && vehicle) {
        contactDealerClick = localContactDealerClick;
    } else if (vehicle) {
        const vehicleUrl = getUscUrl(
            commonSettings,
            uscSettings.detailPageUrl,
            context,
            getUscVehicleUrlInfoFromVehicle(vehicle),
        );
        const modalTitle = getUscLabel({ commonSettings, uscSettings }, "uscContactDealerModalTitle");
        contactDealerClick = (): void => {
            tracking();
            dispatch(
                showModal({
                    type: MODAL_USC_FORM,
                    vehicleForSaleId: vehicle?.id || "",
                    uscContext: context,
                    modalTitle,
                    externalData: {
                        externalPriceInfo: null,
                        vehicleUrl,
                    },
                    vehicle,
                    dealer: dealer || undefined,
                }),
            );
        };
    }

    return contactDealerClick
        ? {
              label: contactLabel,
              onClick: contactDealerClick,
              type: UscCtaTypeEnum.ContactDealer,
          }
        : null;
};

export const getContactDealerPhoneCta = (params: UscNavigationParamsType): UscCtaType | null => {
    const { dealer, context, uscSettings, commonSettings, trackingLocation } = params;
    // Edge case: We use the tracking location to only shown the contact dealer button on the mobile nav modal
    if (!dealer || !dealer.phoneNumber || trackingLocation !== UscTrackingLocation.MobileNavModal) return null;

    const contactLabel = getUscLabel({ uscSettings, commonSettings }, "uscCallDealerCta");

    const tracking = (): void => {
        trackAemContactDealerCtas(DealerInfoType.PhoneNumber, dealer, context);
    };

    return {
        label: contactLabel,
        href: `tel:${dealer.phoneNumber}`,
        icon: UscCtaIcon.Phone,
        type: UscCtaTypeEnum.CallDealer,
        onClick: tracking,
    };
};

export const getMobileNavCta = (params: UscNavigationParamsType): UscCtaType | null => {
    const { dispatch, context, uscSettings, commonSettings, trackingLocation } = params;
    const mobileNavLabel = getUscLabel({ uscSettings, commonSettings }, "uscMobileNavCta");

    return {
        label: mobileNavLabel,
        onClick: () => {
            trackCta("open-cta-overlay", trackingLocation, context, false);
            dispatch(
                showModal({
                    type: MODAL_USC_MOBILE_NAV_CTA,
                }),
            );
        },
    };
};

/**
 * If the dynamic CTA functionality is enabled, the CTAs we show are
 * based on the dealer operations we receive from A2D. For more context,
 * see https://toyota-europe.atlassian.net/l/cp/8sKMerSX
 */
export const enableBasedOnOperation = (
    operation: DealerOperation,
    dealer: DealerResultType | null,
    uscSettings: UscSettingsType,
): boolean => {
    if (!uscSettings.enableDynamicCtas) return true;

    if (!dealer) return false; // If we dont have a dealer, we cant enable the CTA

    const { operations } = dealer;

    return operations.includes(operation);
};

/**
 * Hides the buy CTA for Toshiko dealers on cash if the hideOnCash flag is set to true
 */
export const shouldRenderBuyCta = (activeFinanceOption: FinanceOptionType, hideOnCash: boolean): boolean =>
    !(activeFinanceOption === "cash" && hideOnCash);

export const shouldRenderReserveCta = (activeFinanceOption: FinanceOptionType, hideOnMonthly: boolean): boolean =>
    !(activeFinanceOption === "monthly" && hideOnMonthly);

export const getBuyCta = (params: UscNavigationParamsType): UscCtaType | null => {
    const { vehicle, uscSettings, dealer, activeFinanceOption } = params;
    const { enableBuy, dynamicCtasHideBuyCtaOnCash } = params.uscSettings;

    const hasBuy =
        enableBuy &&
        enableBasedOnOperation(DealerOperation.UsedCarOnlineFinancingPanE, dealer, uscSettings) &&
        shouldRenderBuyCta(activeFinanceOption, dynamicCtasHideBuyCtaOnCash);

    if (!vehicle || !hasBuy) return null;

    return getNavToExtrasCta(params, UscCheckoutType.Buy);
};

export const getReserveCta = (params: UscNavigationParamsType): UscCtaType | null => {
    const { vehicle, uscSettings, dealer } = params;
    const { enableReserve, enableLocalReservation, dynamicCtasHideReserveCtaOnMonthly } = params.uscSettings;
    if (!vehicle) return null;

    const hasReserve =
        enableReserve &&
        enableBasedOnOperation(DealerOperation.UsedCarOnlineReservationPanE, dealer, uscSettings) &&
        shouldRenderReserveCta(params.activeFinanceOption, dynamicCtasHideReserveCtaOnMonthly);

    if (hasReserve) {
        return getNavToExtrasCta(params, UscCheckoutType.Reserve);
    }

    if (!enableReserve && enableLocalReservation) {
        const localReservationCta = getNavToLocalReservationCta(params);
        if (localReservationCta) return localReservationCta;
    }

    return null;
};

// ----------------------------------------------------------------------
// Wrapper functions to get the required ctas
// ----------------------------------------------------------------------

/**
 * Get the valid cta based on the uscCtaType
 *
 * @param ctaType - The cta type of the cta you want
 */
const getCtaBasedOnCtaType = (params: UscNavigationParamsType, ctaType: UscCtaTypeEnum): UscCtaType | null => {
    const buyCta = getBuyCta(params);
    const reserveCta = getReserveCta(params);
    const contactDealer = getContactDealerCta(params);
    const contactDealerPhone = getContactDealerPhoneCta(params);
    const navToCheckout = getNavToCheckoutCta(params);
    const backToCarFilterCta = getNavToFilterCta(params);

    if (ctaType === UscCtaTypeEnum.Buy && buyCta) return buyCta;
    else if (ctaType === UscCtaTypeEnum.Reserve && reserveCta) return reserveCta;
    else if (ctaType === UscCtaTypeEnum.ContactDealer && contactDealer) return contactDealer;
    else if (ctaType === UscCtaTypeEnum.CallDealer && contactDealerPhone) return contactDealerPhone;
    else if (ctaType === UscCtaTypeEnum.NavToCheckout && navToCheckout) return navToCheckout;
    else if (ctaType === UscCtaTypeEnum.BackToCarFilter && backToCarFilterCta) return backToCarFilterCta;

    return null;
};

/**
 * Get the ctas from a list of uscCtaTypes
 *
 * @param ctaTypes - List of uscCtaTypes where to search for valid ctas
 */
const getCtasListBasedOnCtaTypes = (params: UscNavigationParamsType, ctaTypes: UscCtaTypeEnum[]): UscCtaType[] => {
    const ctaList: UscCtaType[] = [];

    ctaTypes.forEach((ctaType) => {
        const cta = getCtaBasedOnCtaType(params, ctaType);
        if (cta) ctaList.push(cta);
    });

    return ctaList;
};

// Mapping of the cta's the NMSC's can configure with the 'ctas' setting, based on the vehicle status.
const allowedPdpCtas: Record<DfVehicleStatus, UscCtaTypeEnum[]> = {
    [DfVehicleStatus.Available]: [
        UscCtaTypeEnum.Reserve,
        UscCtaTypeEnum.Buy,
        UscCtaTypeEnum.ContactDealer,
        UscCtaTypeEnum.CallDealer,
    ],
    [DfVehicleStatus.Reserved]: [UscCtaTypeEnum.ContactDealer, UscCtaTypeEnum.CallDealer],
    [DfVehicleStatus.Sold]: [],
};

/**
 * Returns a list of CTA types that should be shown based on the current step and vehicle status code
 */
export const getCtaTypes = (params: UscNavigationParamsType): UscCtaTypeEnum[] => {
    const { vehicle, uscSettings } = params;
    const { ctas } = uscSettings;
    const vehicleStatus = vehicle?.vehicleStatus.code;

    const ctaTypesList: UscCtaTypeEnum[] = [];
    if (params.currentStep === UscNavStepEnum.Pdp) {
        // We add the backToCarFilter cta when the vehicle status is reserved or sold
        if (vehicleStatus === DfVehicleStatus.Reserved || vehicleStatus === DfVehicleStatus.Sold) {
            ctaTypesList.push(UscCtaTypeEnum.BackToCarFilter);
        }

        // We add the NMSC's specific configured ctas based on the vehicle status
        ctas.forEach((ctaType) => {
            if (vehicleStatus && allowedPdpCtas[vehicleStatus].includes(ctaType)) ctaTypesList.push(ctaType);
        });
    } else {
        // On the extras page you can only navigate into the checkout flow
        ctaTypesList.push(UscCtaTypeEnum.NavToCheckout);
    }

    return ctaTypesList;
};

type GetBasketCtasReturnType = { primary: UscCtaType | null; secondary: UscCtaType[] };

/**
 * Get the primary and secondary CTA's to be shown in the basket.
 *
 */
export const getBasketCtas = (params: UscNavigationParamsType): GetBasketCtasReturnType => {
    const ctaTypes = getCtaTypes(params);
    const ctaList = getCtasListBasedOnCtaTypes(params, ctaTypes);

    // The first available item of the ctas setting will become the primary cta.
    // The reset of ctas setting will become secondary ctas
    const [primaryCta, ...secondaryCtas] = ctaList;

    return {
        primary: primaryCta,
        secondary: secondaryCtas,
    };
};

/**
 * Get the mobile footer cta
 */
export const getMobileFooterCta = (params: UscNavigationParamsType): UscCtaType | null => {
    const { vehicle } = params;
    const vehicleStatus = vehicle?.vehicleStatus.code;

    // We always show the Mobile Navigation Cta except in 2 cases
    // 1. On the extras step we always show 1 button (navigationToCheckout)
    // 2. When a vehicle is sold, we only show 1 button on the PDP (goBackToCarFilter)
    if (params.currentStep === UscNavStepEnum.Extras || vehicleStatus === DfVehicleStatus.Sold) {
        const ctaTypes = getCtaTypes(params);
        const ctaList = getCtasListBasedOnCtaTypes(params, ctaTypes);
        return ctaList[0];
    }

    return getMobileNavCta(params);
};

type GetMobileNavCtaModalCtasReturnType = {
    primary: UscCtaType | null;
    secondary: UscCtaType[];
    tertiary: UscCtaType | null;
};

/**
 * Get the main, secondary and tertiary CTAs to be shown in the MobileNavCtaModal.
 */
export const getMobileNavModalCtas = (params: UscNavigationParamsType): GetMobileNavCtaModalCtasReturnType => {
    const returnObj: GetMobileNavCtaModalCtasReturnType = { primary: null, secondary: [], tertiary: null };
    const ctaTypes = getCtaTypes(params);

    // The first available item of the ctas settings will become primary cta.
    // The reset will become secondary ctas
    // The tertiary cta is the call dealer phone cta if available
    const mobileCtaList = getCtasListBasedOnCtaTypes(params, ctaTypes);
    const [primaryCta, ...secondaryCtas] = mobileCtaList.filter((cta) => cta.type !== UscCtaTypeEnum.CallDealer);

    returnObj.primary = primaryCta;
    returnObj.secondary = secondaryCtas;
    returnObj.tertiary = mobileCtaList.find((cta) => cta.type === UscCtaTypeEnum.CallDealer) || null;

    return returnObj;
};
